<template>
  <div class="tier-reason">
    <div class="tier-reason__content">
      <div class="tier-reason__column">
        <div class="column-header">
          <q-icon name="error_outline" size="14px" />
          <span class="column-title">Причины невыполнения</span>
          <q-badge v-if="reasons.length" :label="filteredReasons.length + '/' + reasons.length" color="grey-4" text-color="grey-8" />
          <q-btn
            v-if="selectedReasons.length > 0"
            icon="delete"
            flat
            dense
            round
            size="xs"
            color="negative"
            @click="confirmDeleteSelectedReasons"
          >
            <q-tooltip>Удалить выбранные ({{ selectedReasons.length }})</q-tooltip>
          </q-btn>
          <q-btn
            icon="add"
            flat
            dense
            round
            size="xs"
            color="primary"
            @click="openReasonDialog()"
          >
            <q-tooltip>Добавить</q-tooltip>
          </q-btn>
        </div>
        <div v-if="reasons.length" class="search-container">
          <q-input
            v-model="searchQuery"
            dense
            outlined
            placeholder="Поиск..."
            clearable
            class="search-input"
          >
            <template v-slot:prepend>
              <q-icon name="search" size="16px" />
            </template>
          </q-input>
        </div>
        <q-table
          v-if="!isLoading && filteredReasons.length > 0"
          :rows="filteredReasons"
          :columns="columns"
          row-key="reason_id"
          :selected="selectedReasonsRows"
          selection="multiple"
          @selection="onReasonSelection"
          :rows-per-page-options="[0]"
          hide-pagination
          flat
          dense
          class="column-table"
        >
          <template v-slot:body="props">
            <q-tr :props="props">
              <q-td auto-width>
                <q-checkbox
                  :model-value="selectedReasons.includes(props.row.reason_id)"
                  @update:model-value="(val) => toggleReasonSelection(props.row.reason_id, val)"
                  @click.stop
                  size="xs"
                  dense
                />
              </q-td>
              <q-td>{{ props.row.reason_name }}</q-td>
              <q-td>{{ props.row.reason_id }}</q-td>
              <q-td auto-width>
                <q-btn
                  icon="edit"
                  flat
                  dense
                  round
                  size="xs"
                  color="primary"
                  @click.stop="openReasonDialog(props.row)"
                >
                  <q-tooltip>Редактировать</q-tooltip>
                </q-btn>
              </q-td>
            </q-tr>
          </template>
        </q-table>
        <div v-else-if="!isLoading && filteredReasons.length === 0" class="empty-state">
          <q-icon name="search_off" color="grey-5" />
          <p>{{ searchQuery ? 'Ничего не найдено' : 'Нет причин' }}</p>
        </div>
        <div v-else class="loading-container">
          <q-spinner color="primary" size="30px" />
        </div>
      </div>
    </div>

    <!-- Диалог подтверждения -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>

        <q-card-section>
          {{ confirmDialog.message }}
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useAppStore } from '@/services/store/app';
import {
  getReasons,
  insertReason,
  updateReason,
  deleteReason
} from "@/views/TierReason/services";
import { MODAL_NAMES_AIDAR } from "@/constants/index_aidar";

export default {
  name: "TierReason",
  data() {
    return {
      reasons: [],
      isLoading: false,
      searchQuery: '',
      selectedReasons: [],
      columns: [
        { name: 'reason_name', label: 'Название', field: 'reason_name', align: 'left', sortable: true },
        { name: 'reason_id', label: 'ID', field: 'reason_id', align: 'left', sortable: true },
        { name: 'actions', label: '', field: 'actions', align: 'right', sortable: false }
      ],
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
    };
  },
  computed: {
    filteredReasons() {
      if (!this.searchQuery) return this.reasons;
      const query = this.searchQuery.toLowerCase();
      return this.reasons.filter(reason => 
        reason.reason_name.toLowerCase().includes(query) ||
        reason.reason_id.toString().includes(query)
      );
    },
    selectedReasonsRows() {
      return this.reasons.filter(reason => this.selectedReasons.includes(reason.reason_id));
    }
  },
  methods: {
    async loadReasons() {
      this.isLoading = true;
      const res = await getReasons();
      this.isLoading = false;
      if (!res) return;
      this.reasons = res;
    },
    async loadReasonsSilently() {
      const res = await getReasons();
      if (!res) return;
      this.reasons = res;
    },
    openReasonDialog(reason = null) {
      this.showModal(MODAL_NAMES_AIDAR[reason ? 'EditReason' : 'AddReason'], {
        title: reason ? 'Редактировать причину' : 'Добавить причину',
        reason: reason,
        onSubmit: async (data) => {
          let res;
          if (reason) {
            res = await updateReason(data);
          } else {
            res = await insertReason(data);
          }
          if (res) {
            this.showSucceedSnackbar(reason ? 'Причина обновлена' : 'Причина добавлена');
            await this.loadReasonsSilently();
          }
        }
      });
    },
    toggleReasonSelection(reasonId, selected) {
      if (selected) {
        if (!this.selectedReasons.includes(reasonId)) {
          this.selectedReasons.push(reasonId);
        }
      } else {
        const index = this.selectedReasons.indexOf(reasonId);
        if (index > -1) {
          this.selectedReasons.splice(index, 1);
        }
      }
    },
    onReasonSelection({ rows, added, removed }) {
      if (added && added.length > 0) {
        added.forEach(row => {
          if (!this.selectedReasons.includes(row.reason_id)) {
            this.selectedReasons.push(row.reason_id);
          }
        });
      }
      if (removed && removed.length > 0) {
        removed.forEach(row => {
          const index = this.selectedReasons.indexOf(row.reason_id);
          if (index > -1) {
            this.selectedReasons.splice(index, 1);
          }
        });
      }
    },
    confirmDeleteSelectedReasons() {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить ${this.selectedReasons.length} причин?`,
        onConfirm: async () => {
          await this.deleteSelectedReasons();
        }
      };
    },
    async deleteSelectedReasons() {
      const reasonsToDelete = [...this.selectedReasons];
      
      for (const reasonId of reasonsToDelete) {
        await deleteReason(reasonId);
      }

      this.confirmDialog.show = false;
      this.selectedReasons = [];
      this.showSucceedSnackbar(`Удалено ${reasonsToDelete.length} причин`);
      
      await this.loadReasonsSilently();
    },
    async onConfirmDelete() {
      this.confirmDialog.show = false;
      if (this.confirmDialog.onConfirm) {
        await this.confirmDialog.onConfirm();
      }
    }
  },
  mounted() {
    this.loadReasons();
  },
  setup() {
    const { showSucceedSnackbar } = useCustomSnackbar();
    const { showModal } = useAppStore();
    return { showSucceedSnackbar, showModal };
  },
};
</script>

<style scoped>
.tier-reason {
  padding: 8px;
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tier-reason__content {
  display: flex;
  gap: 6px;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.tier-reason__column {
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #ddd;
  overflow: hidden;
  flex: 1;
  min-width: 0;
}

.column-header {
  background: #f0f0f0;
  padding: 4px 8px;
  color: #333;
  font-weight: 600;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border-bottom: 1px solid #ddd;
}

.column-title {
  flex: 1;
}

.search-container {
  padding: 6px 8px;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
}

.search-input {
  font-size: 12px;
}

.search-input :deep(.q-field__control) {
  height: 28px;
  min-height: 28px;
}

.search-input :deep(.q-field__marginal) {
  height: 28px;
}

.search-input :deep(input) {
  font-size: 12px;
  padding: 2px 4px;
}

.column-table {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.column-table :deep(.q-table__container) {
  height: 100%;
}

.column-table :deep(.q-table__top) {
  display: none;
}

.column-table :deep(.q-table thead tr th) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f5f5f5;
  font-weight: 600;
  color: #333;
  font-size: 11px;
  padding: 6px 8px;
  border-bottom: 1px solid #ddd;
}

.column-table :deep(.q-table tbody tr) {
  font-size: 11px;
  cursor: pointer;
}

.column-table :deep(.q-table tbody td) {
  padding: 6px 8px;
  border-bottom: 1px solid #f0f0f0;
}

.column-table :deep(.q-table tbody tr:hover) {
  background: #e8f4fd;
}

.column-table :deep(.q-table__card) {
  box-shadow: none;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  gap: 6px;
  padding: 16px;
}

.empty-state p {
  margin: 0;
  font-size: 11px;
}

.empty-state .q-icon {
  font-size: 28px !important;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 80px;
}

.column-header .q-btn {
  padding: 2px;
  min-width: 24px;
  min-height: 24px;
}

.column-header .q-icon {
  font-size: 14px;
}

.column-header .q-badge {
  font-size: 10px;
  padding: 2px 4px;
}
</style>

